<template>
    <div v-if="loadingError" class="img-loading-error d-flex align-items-center justify-content-center" :class="customClasses">
        <i :class="`fas ${fileIcon} fa-3x text-muted`"></i>
    </div>
    <img v-else :src="source" :width="width" :height="height" :class="customClasses" @load="handleLoad" @error="handleError">
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop } from '@/helpers/Decorators';
import { isString } from 'lodash';
import avatarImg from '@/assets/img/avatar.jpg';

const BLANK_TEMPLATE =
  '<svg width="%{w}" height="%{h}" ' +
  'xmlns="http://www.w3.org/2000/svg" ' +
  'viewBox="0 0 %{w} %{h}" preserveAspectRatio="none">' +
  '<rect width="100%" height="100%" style="fill:%{f};"></rect>' +
  '</svg>';

@Options({
    name: 'ideo-image'
})
export default class IdeoImage extends Vue
{
    @Prop({ default: false })
    public blank: boolean;

    @Prop({ default: 'transparent' })
    public blankColor: string;

    @Prop({ default: 'color', validator: (value: string) => ['color', 'avatar'].includes(value) })
    public blankType: string;

    @Prop({ default: false })
    public block: boolean;

    @Prop({ default: false })
    public center: boolean;

    @Prop({ default: false })
    public fluid: boolean;

    @Prop({ default: false })
    public fluidGrow: boolean;

    @Prop({ default: undefined })
    public height: number;

    @Prop({ default: false })
    public left: boolean;

    @Prop({ default: false })
    public right: boolean;

    @Prop({ default: false, validator: (value: any) => [false, true, 'top', 'right', 'bottom', 'left', 'circle', '0'].includes(value) })
    public rounded: string | boolean;

    @Prop()
    public src: string;

    @Prop({ default: false })
    public thumbnail: boolean;

    @Prop({ default: undefined })
    public width: number;

    public loadingError: boolean = false;

    public get fileIcon(): string
    {
        const map: Record<string, string> = {
            // 📄 dokumenty
            pdf: 'fa-file-pdf',
            doc: 'fa-file-word',
            docx: 'fa-file-word',
            odt: 'fa-file-word',
            rtf: 'fa-file-word',
            txt: 'fa-file-lines',
            md: 'fa-file-lines',

            // 📊 arkusze
            xls: 'fa-file-excel',
            xlsx: 'fa-file-excel',
            csv: 'fa-file-csv',
            ods: 'fa-file-excel',

            // 📽 prezentacje
            ppt: 'fa-file-powerpoint',
            pptx: 'fa-file-powerpoint',
            odp: 'fa-file-powerpoint',

            // 🖼 obrazy
            jpg: 'fa-file-image',
            jpeg: 'fa-file-image',
            png: 'fa-file-image',
            gif: 'fa-file-image',
            webp: 'fa-file-image',
            svg: 'fa-file-image',
            bmp: 'fa-file-image',
            tiff: 'fa-file-image',

            // 🎬 wideo
            mp4: 'fa-file-video',
            mov: 'fa-file-video',
            avi: 'fa-file-video',
            mkv: 'fa-file-video',
            webm: 'fa-file-video',

            // 🎵 audio
            mp3: 'fa-file-audio',
            wav: 'fa-file-audio',
            ogg: 'fa-file-audio',
            flac: 'fa-file-audio',
            aac: 'fa-file-audio',
            m4a: 'fa-file-audio',

            // 🗜 archiwa
            zip: 'fa-file-zipper',
            rar: 'fa-file-zipper',
            '7z': 'fa-file-zipper',
            tar: 'fa-file-zipper',
            gz: 'fa-file-zipper',
            bz2: 'fa-file-zipper',
            xz: 'fa-file-zipper',

            // 💻 kod / dane
            json: 'fa-file-code',
            xml: 'fa-file-code',
            yml: 'fa-file-code',
            yaml: 'fa-file-code',
            html: 'fa-file-code',
            css: 'fa-file-code',
            js: 'fa-file-code',
            ts: 'fa-file-code',
        };

        const extension = new URL(this.source).pathname.split('.').pop();

        return map[extension] || 'fa-file';
    }

    public get source(): string
    {
        return this.flag(this.blank) ? this.blankImgSrc() : this.src;
    }

    public get customClasses(): Record<string, boolean>
    {
        return {
            'img-thumbnail': this.flag(this.thumbnail),
            'img-fluid': this.flag(this.fluid) || this.flag(this.fluidGrow),
            'float-start': this.flag(this.left),
            'float-end': this.flag(this.right),
            'mx-auto': this.flag(this.center),
            'd-block': this.flag(this.block) || this.flag(this.center),
            'w-100': this.flag(this.fluidGrow),
            'rounded': this.flag(this.rounded),
            [`rounded-${this.rounded}`]: isString(this.rounded) && this.rounded.length > 0
        };
    }

    public flag(value: any): boolean
    {
        return value !== false;
    }

    public blankImgSrc(): string
    {
        switch (this.blankType)
        {
            case 'color':
            {
                const src = encodeURIComponent(
                    BLANK_TEMPLATE.replace('%{w}', (this.width || 1).toString())
                        .replace('%{h}', (this.height || 1).toString())
                        .replace('%{f}', this.blankColor)
                );

                return `data:image/svg+xml;charset=UTF-8,${src}`;
            }
            case 'avatar':
            {
                return avatarImg;
            }
        }
    }

    public handleLoad(): void
    {
        this.loadingError = false;
    }

    public handleError(event: Event): void
    {
        this.loadingError = true;
    }
}
</script>
